import { Box, Typography, IconButton, Link } from "@mui/material";
import RemoveRounded from "@mui/icons-material/RemoveRounded";
import AddRounded from "@mui/icons-material/AddRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { getRouteUrl } from "../../../helpers/routing/getRouteUrl";
import { ROUTE_SINGLE_PRODUCT } from "../../../routers/routes";
import {useTranslation} from "react-i18next";
import {PRODUCT_INFO} from "../../../locales/components/namespaces";

const TOKENS = {
  grayBg: "#E9E9E9",
  white: "#FCFCFC",
  black: "#111111",
  magenta: "#E5007E",
  pillRadius: 80,
};

export default function CartItemRow({ item, onInc, onDec, onRemove }) {
    const { t } = useTranslation(PRODUCT_INFO);
  const id = item?.id;
  const code = item?.code;
  const title = item?.title ?? item?.name ?? "";
  const image = item?.image ?? "";
  const unitPrice = item?.unitPrice ?? item?.price ?? 0;
  const quantity = item?.quantity ?? 0;
    const availability = item?.availabilityLabel;

  const lineTotal = Number(unitPrice || 0) * Number(quantity || 0);
  const history = useHistory();
  const dispatch = useDispatch();

  const fmtEUR = (n) =>
    Number.isFinite(n)
      ? new Intl.NumberFormat("el-GR", {
          style: "currency",
          currency: "EUR",
        })
          .format(n)
          .replace(/\s/g, " ")
      : "-";

  const handleInc = () => {
    if (typeof onInc === "function") {
      onInc(id);
      return;
    }
    dispatch({ type: "CART/INCREASE_QTY", payload: { id } });
  };

  const handleDec = () => {
    if (typeof onDec === "function") {
      onDec(id);
      return;
    }
    dispatch({ type: "CART/DECREASE_QTY", payload: { id } });
  };

  const handleRemove = () => {
    if (typeof onRemove === "function") {
      onRemove(id);
      return;
    }
    dispatch({ type: "CART/REMOVE_ITEM", payload: { id } });
  };

  const goToProduct = () => {
    history.push(getRouteUrl(ROUTE_SINGLE_PRODUCT, { id }));
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "flex-start",
        gap: 2,
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: { xs: "block", lg: "none" },
          bgcolor: TOKENS.grayBg,
          borderRadius: 2,
          px: 1.75,
          py: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.5 }}>
          <Box
            sx={{
              width: 82,
              height: 72,
              borderRadius: 1,
              overflow: "hidden",
              bgcolor: TOKENS.grayBg,
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src={image}
              alt={title}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
              onClick={goToProduct}
            />
          </Box>
          <Link
            component="button"
            onClick={goToProduct}
            sx={{
              textDecoration: "none",
              color: TOKENS.black,
              fontFamily: '"Roboto Condensed", system-ui, sans-serif',
              fontWeight: 700,
              fontSize: 12,
              lineHeight: 1.2,
              whiteSpace: "normal",
              wordBreak: "break-word",
              display: "flex",
              flexDirection: "column",
              textAlign: "left",
              p: 0,
            }}
          >
            <span>{code}</span>
            <span>{title}</span>
          </Link>
        </Box>

        {availability && (
          <Box
            sx={{
              mb: 1.25,
              px: 1.5,
              py: 0.25,
              borderRadius: TOKENS.pillRadius,
              bgcolor: availability.color || "#38B60E",
              display: "inline-flex",
            }}
          >
            <Typography
              sx={{
                fontFamily: '"Zen Maru Gothic", system-ui, sans-serif',
                fontWeight: 900,
                fontSize: 12,
                lineHeight: 1.2,
                color: TOKENS.white,
              }}
            >
               {t(availability.labelKey)}
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            columnGap: 2,
            rowGap: 1,
          }}
        >
          <Typography
            sx={{
              fontFamily: '"Roboto Condensed", system-ui, sans-serif',
              fontWeight: 700,
              fontSize: 16,
              lineHeight: 1.2,
              color: TOKENS.black,
            }}
          >
            {fmtEUR(unitPrice)}
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 1,
            }}
          >
            <IconButton
              size="small"
              onClick={handleDec}
              sx={{ width: 28, height: 28 }}
            >
              <RemoveRounded sx={{ fontSize: 18 }} />
            </IconButton>

            <Box
              sx={{
                minWidth: 34,
                height: 34,
                borderRadius: 1.2,
                bgcolor: TOKENS.white,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 1,
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"Roboto Condensed", system-ui, sans-serif',
                  fontWeight: 700,
                  fontSize: 14,
                  lineHeight: 1.2,
                  color: TOKENS.black,
                  textAlign: "center",
                }}
              >
                {quantity}
              </Typography>
            </Box>

            <IconButton
              size="small"
              onClick={handleInc}
              sx={{ width: 28, height: 28 }}
            >
              <AddRounded sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          <Typography
            sx={{
              fontFamily: '"Roboto Condensed", system-ui, sans-serif',
              fontWeight: 700,
              fontSize: 16,
              lineHeight: 1.2,
              color: TOKENS.black,
              textAlign: "right",
              ml: "auto",
            }}
          >
            {fmtEUR(lineTotal)}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: { xs: "none", lg: "block" },
          bgcolor: TOKENS.grayBg,
          borderRadius: 2,
          px: { lg: 2.5 },
          py: { lg: 1.2 },
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "minmax(0,3fr) 250px 200px 200px 110px",
            },
            rowGap: { xs: 1.5, md: 0 },
            alignItems: "center",
          }}
        >
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}
          >
            <Box
              sx={{
                width: 82,
                height: 72,
                borderRadius: 1,
                overflow: "hidden",
                bgcolor: TOKENS.grayBg,
                flexShrink: 0,
                cursor: "pointer",
              }}
            >
              <Box
                component="img"
                src={image}
                alt={title}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
            </Box>

            <Link
              component="button"
              onClick={goToProduct}
              sx={{
                textDecoration: "none",
                color: TOKENS.black,
                fontFamily: '"Roboto Condensed", system-ui, sans-serif',
                fontWeight: 700,
                fontSize: 12,
                lineHeight: 1.2,
                whiteSpace: "normal",
                wordBreak: "break-word",
                display: "flex",
                flexDirection: "column",
                textAlign: "left",
                p: 0,
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"Roboto Condensed", system-ui, sans-serif',
                  fontWeight: 700,
                  fontSize: 12,
                  lineHeight: 1.2,
                }}
              >
                ΚΩΔΙΚΟΣ: {code}
              </Typography>

              <Typography
                sx={{
                  fontFamily: '"Roboto Condensed", system-ui, sans-serif',
                  fontWeight: 400,
                  fontSize: 14,
                  lineHeight: 1.2,
                }}
              >
                {title}
              </Typography>
            </Link>
          </Box>

          <Box
            sx={{
              justifySelf: { xs: "flex-start", md: "center" },
              mr: { md: 2.5 },
            }}
          >
            {availability && (
              <Box
                sx={{
                  px: 1.5,
                  py: 0.25,
                  borderRadius: TOKENS.pillRadius,
                  bgcolor: availability.color || "#38B60E",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: '"Zen Maru Gothic", system-ui, sans-serif',
                    fontWeight: 900,
                    fontSize: 12,
                    lineHeight: 1.2,
                    color: TOKENS.white,
                  }}
                >
                  {t(availability.labelKey)}
                </Typography>
              </Box>
            )}
          </Box>

          <Typography
            sx={{
              fontFamily: '"Roboto Condensed", system-ui, sans-serif',
              fontWeight: 700,
              fontSize: 16,
              lineHeight: 1.2,
              color: TOKENS.black,
              justifySelf: { xs: "flex-start", md: "center" },
            }}
          >
            {fmtEUR(unitPrice)}
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: { xs: "flex-start", md: "center" },
              gap: 1,
            }}
          >
            <IconButton
              size="small"
              onClick={handleDec}
              sx={{ width: 28, height: 28 }}
            >
              <RemoveRounded sx={{ fontSize: 18 }} />
            </IconButton>

            <Box
              sx={{
                minWidth: 34,
                height: 34,
                borderRadius: 1.2,
                bgcolor: TOKENS.white,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 1,
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"Roboto Condensed", system-ui, sans-serif',
                  fontWeight: 700,
                  fontSize: 14,
                  lineHeight: 1.2,
                  color: TOKENS.black,
                  textAlign: "center",
                }}
              >
                {quantity}
              </Typography>
            </Box>

            <IconButton
              size="small"
              onClick={handleInc}
              sx={{ width: 28, height: 28 }}
            >
              <AddRounded sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          <Typography
            sx={{
              fontFamily: '"Roboto Condensed", system-ui, sans-serif',
              fontWeight: 700,
              fontSize: 16,
              lineHeight: 1.2,
              textAlign: "right",
              color: TOKENS.black,
              minWidth: 80,
              justifySelf: { xs: "flex-start", md: "flex-end" },
            }}
          >
            {fmtEUR(lineTotal)}
          </Typography>
        </Box>
      </Box>

      <IconButton
        size="small"
        onClick={handleRemove}
        sx={{
          width: 32,
          height: 32,
          color: TOKENS.magenta,
          alignSelf: "center",
          "&:hover": { bgcolor: "#c9006b", color: TOKENS.white },
        }}
      >
        <CloseRounded sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );
}
